import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";
import { Toggle } from "../ui/Toggle";

type Op = {
  username: string;
  display_name: string | null;
  email: string | null;
  role: "admin" | "operator";
  active: boolean;
  last_login_at: string | null;
};

type PwdReveal = { username: string; password: string; kind: "create" | "reset" };

type PermissionItem = { key: string; label: string; description: string; granted: boolean };
type PermissionsView = { username: string; is_admin: boolean; permissions: PermissionItem[] };

type Props = {
  open: boolean;
  onClose: () => void;
  currentUsername: string;
};

export function UsersAdmin({ open, onClose, currentUsername }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"list" | "create">("list");
  const [newUsername, setNewUsername] = useState("");
  const [newDisplay, setNewDisplay] = useState("");
  const [newRole, setNewRole] = useState<"operator" | "admin">("operator");
  const [error, setError] = useState<string | null>(null);
  const [pwd, setPwd] = useState<PwdReveal | null>(null);
  const [copied, setCopied] = useState(false);
  const [permsFor, setPermsFor] = useState<string | null>(null);

  const { data: ops, isLoading } = useQuery<Op[]>({
    queryKey: ["operators"],
    queryFn: async () => (await api.get("/api/operators")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setError(null);
    setNewUsername("");
    setNewDisplay("");
    setNewRole("operator");
    setPwd(null);
    setPermsFor(null);
  }, [open]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["operators"] });

  const createMut = useMutation({
    mutationFn: async () =>
      (
        await api.post("/api/operators", {
          username: newUsername.trim(),
          display_name: newDisplay.trim() || null,
          role: newRole,
        })
      ).data,
    onSuccess: (d) => {
      invalidate();
      setMode("list");
      setPwd({ username: d.operator.username, password: d.initial_password, kind: "create" });
    },
    onError: (e) => setError(errMsg(e)),
  });

  const resetMut = useMutation({
    mutationFn: async (username: string) =>
      (await api.post(`/api/operators/${username}/reset-password`)).data,
    onSuccess: (d) => setPwd({ username: d.username, password: d.new_password, kind: "reset" }),
    onError: (e) => setError(errMsg(e)),
  });

  const patchMut = useMutation({
    mutationFn: async (v: { username: string; body: Record<string, unknown> }) =>
      (await api.patch(`/api/operators/${v.username}`, v.body)).data,
    onSuccess: () => invalidate(),
    onError: (e) => setError(errMsg(e)),
  });

  const busy = createMut.isPending || resetMut.isPending || patchMut.isPending;

  async function copyPwd() {
    if (!pwd) return;
    try {
      await navigator.clipboard.writeText(pwd.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={mode === "create" ? "Nuovo operatore" : "Gestione utenti"}
        footer={
          mode === "create" ? (
            <>
              <Button variant="ghost" block onClick={() => setMode("list")} disabled={busy}>
                Annulla
              </Button>
              <Button
                variant="primary"
                block
                disabled={busy || newUsername.trim().length < 2}
                onClick={() => {
                  setError(null);
                  createMut.mutate();
                }}
              >
                Crea utente
              </Button>
            </>
          ) : (
            <Button variant="primary" block onClick={() => setMode("create")}>
              <Icon name="plus" size={14} /> Crea utente
            </Button>
          )
        }
      >
        {error && (
          <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
            {error}
          </Banner>
        )}

        {mode === "create" ? (
          <div>
            <Field label="Username" hint="Minimo 2 caratteri · usato per il login">
              <input
                className="tcg-input tcg-mono"
                value={newUsername}
                autoFocus
                placeholder="es. mario"
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </Field>
            <Field label="Nome visualizzato">
              <input
                className="tcg-input"
                value={newDisplay}
                placeholder="es. Mario Rossi"
                onChange={(e) => setNewDisplay(e.target.value)}
              />
            </Field>
            <Field label="Ruolo">
              <select
                className="tcg-select"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "operator" | "admin")}
              >
                <option value="operator">Operatore</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Banner tone="info" icon="lock">
              La password iniziale verrà generata e mostrata una sola volta dopo la creazione.
            </Banner>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {isLoading && <div style={{ color: "var(--tcg-muted)", fontSize: 13 }}>Caricamento…</div>}
            {ops?.map((o) => (
              <OperatorRow
                key={o.username}
                op={o}
                isSelf={o.username === currentUsername}
                busy={busy}
                onToggleActive={() =>
                  patchMut.mutate({ username: o.username, body: { active: !o.active } })
                }
                onToggleRole={() =>
                  patchMut.mutate({
                    username: o.username,
                    body: { role: o.role === "admin" ? "operator" : "admin" },
                  })
                }
                onReset={() => {
                  setError(null);
                  resetMut.mutate(o.username);
                }}
                onPerms={() => {
                  setError(null);
                  setPermsFor(o.username);
                }}
              />
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={pwd !== null}
        onClose={() => setPwd(null)}
        tone="warn"
        title={pwd?.kind === "create" ? "Password iniziale" : "Nuova password"}
        footer={
          <Button variant="primary" block onClick={() => setPwd(null)}>
            Fatto
          </Button>
        }
      >
        <Banner tone="warn" icon="lock" style={{ marginBottom: 12 }}>
          Copiala adesso: per sicurezza non sarà più visibile dopo la chiusura.
        </Banner>
        <div style={{ fontSize: 12, color: "var(--tcg-muted)", marginBottom: 6 }}>
          Utente <span className="tcg-mono">{pwd?.username}</span>
        </div>
        <div
          onClick={copyPwd}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "var(--tcg-bg)",
            border: "1px solid var(--tcg-border)",
            borderRadius: 12,
            padding: "12px 14px",
            cursor: "pointer",
          }}
        >
          <div
            className="tcg-mono"
            style={{ flex: 1, fontSize: 16, fontWeight: 800, color: "var(--tcg-navy)", letterSpacing: "0.02em" }}
          >
            {pwd?.password}
          </div>
          <Badge tone={copied ? "success" : "navy"}>{copied ? "COPIATA" : "COPIA"}</Badge>
        </div>
      </Modal>

      <PermissionsEditor
        username={permsFor}
        onClose={() => setPermsFor(null)}
        onSaved={() => setPermsFor(null)}
      />
    </>
  );
}

function PermissionsEditor({
  username,
  onClose,
  onSaved,
}: {
  username: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = username !== null;
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<PermissionsView>({
    queryKey: ["operator-perms", username],
    queryFn: async () => (await api.get(`/api/operators/${username}/permissions`)).data,
    enabled: open,
  });

  useEffect(() => {
    if (data) setGranted(new Set(data.permissions.filter((p) => p.granted).map((p) => p.key)));
    setError(null);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async () =>
      (await api.put(`/api/operators/${username}/permissions`, { permissions: [...granted] })).data,
    onSuccess: () => onSaved(),
    onError: (e) => setError(errMsg(e)),
  });

  const isAdmin = data?.is_admin ?? false;

  function toggle(key: string, next: boolean) {
    setGranted((cur) => {
      const n = new Set(cur);
      if (next) n.add(key);
      else n.delete(key);
      return n;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Permessi · ${username ?? ""}`}
      footer={
        <>
          <Button variant="ghost" block onClick={onClose} disabled={saveMut.isPending}>
            Chiudi
          </Button>
          <Button
            variant="primary"
            block
            disabled={saveMut.isPending || isAdmin || isLoading}
            onClick={() => {
              setError(null);
              saveMut.mutate();
            }}
          >
            Salva permessi
          </Button>
        </>
      }
    >
      {error && (
        <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}
      {isLoading && <div style={{ color: "var(--tcg-muted)", fontSize: 13 }}>Caricamento…</div>}
      {isAdmin && (
        <Banner tone="info" icon="lock" style={{ marginBottom: 12 }}>
          Gli admin hanno sempre tutti i permessi. I toggle sono di sola lettura.
        </Banner>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data?.permissions.map((p) => {
          const on = isAdmin || granted.has(p.key);
          return (
            <div
              key={p.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid var(--tcg-border)",
                borderRadius: 12,
                padding: "10px 12px",
                opacity: isAdmin ? 0.7 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--tcg-ink)" }}>{p.label}</div>
                <div style={{ fontSize: 11, color: "var(--tcg-muted)", marginTop: 1 }}>{p.description}</div>
                <div className="tcg-mono" style={{ fontSize: 9, color: "var(--tcg-muted-soft)", marginTop: 2 }}>
                  {p.key}
                </div>
              </div>
              <Toggle on={on} label={p.label} onChange={(next) => !isAdmin && toggle(p.key, next)} />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function OperatorRow({
  op,
  isSelf,
  busy,
  onToggleActive,
  onToggleRole,
  onReset,
  onPerms,
}: {
  op: Op;
  isSelf: boolean;
  busy: boolean;
  onToggleActive: () => void;
  onToggleRole: () => void;
  onReset: () => void;
  onPerms: () => void;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--tcg-border)",
        borderRadius: 14,
        padding: "12px 14px",
        background: op.active ? "var(--tcg-card)" : "var(--tcg-bg)",
        opacity: op.active ? 1 : 0.72,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--tcg-ink)" }}>
            {op.display_name || op.username}
            {isSelf && (
              <span className="tcg-mono" style={{ fontSize: 9, color: "var(--tcg-muted)", marginLeft: 6 }}>
                (tu)
              </span>
            )}
          </div>
          <div className="tcg-mono" style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 1 }}>
            @{op.username}
          </div>
        </div>
        <Badge tone={op.role === "admin" ? "yellow" : "ghost"}>{op.role.toUpperCase()}</Badge>
        <Badge tone={op.active ? "success" : "danger"}>{op.active ? "ATTIVO" : "OFF"}</Badge>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <Button variant="secondary" size="small" disabled={busy} onClick={onReset}>
          <Icon name="lock" size={12} /> Reset
        </Button>
        <Button variant="secondary" size="small" disabled={busy} onClick={onPerms}>
          <Icon name="tag" size={12} /> Permessi
        </Button>
        <Button variant="secondary" size="small" disabled={busy || isSelf} onClick={onToggleRole}>
          <Icon name="user" size={12} /> {op.role === "admin" ? "→ Operatore" : "→ Admin"}
        </Button>
        <Button
          variant={op.active ? "danger" : "primary"}
          size="small"
          disabled={busy || isSelf}
          onClick={onToggleActive}
        >
          <Icon name={op.active ? "x" : "check"} size={12} /> {op.active ? "Disattiva" : "Attiva"}
        </Button>
      </div>
    </div>
  );
}
